import React from 'react';

import { selectCartItems } from '@entities/cart';
import {
	changeAmount,
	changeItemQuantity,
	changeMetadata,
	changeStatus,
	selectItem,
} from '@entities/cart';
import { removeFromCart } from '@entities/cart';
import type { AxiosError } from 'axios';
import { debounce } from 'lodash';

import { useAppDispatch, useAppSelector, useDialog } from '@shared/hooks';

import { shopService } from 'library/api/shopService';

export const useProducts = () => {
	const items = useAppSelector(selectCartItems);

	return {
		items,
	};
};

export const useUpdateQuantity = (id: string) => {
	const dispatch = useAppDispatch();
	const item = useAppSelector((state) => selectItem(state, id));
	const [error, setError] = React.useState(false);
	const { notification } = useDialog();

	const updateQuantity = async (id: string, value: string) => {
		setError(false);
		dispatch(changeStatus('loading'));

		try {
			const response = await shopService.updateBasketItem(id, value);
			if (!response.data) {
				throw response;
			}
			dispatch(changeAmount({ amount: response.data.amount }));
			dispatch(changeStatus('loaded'));
			if (response.data.delivery_metadata) {
				dispatch(changeMetadata(response.data.delivery_metadata));
			}
		} catch (error) {
			const err = error as AxiosError<any>;
			let message = '';

			if (
				err.response?.status === 400 &&
				err.response?.data[0] === 'Order count more than storage items on store!'
			) {
				message = `Недостаточно товара на складе`;
			} else {
				message = 'Не удалось изменить количество товара';
			}
			dispatch(changeStatus('error'));
			setError(true);

			notification({
				title: 'Ошибка',
				text: message,
				confirmText: 'Ок',
			});
		}
	};

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debouncedUpdate = React.useCallback(debounce(updateQuantity, 500), []);

	const handleChange = (value: string) => {
		if (item?.count.toString() !== value) {
			dispatch(changeItemQuantity({ quantity: value, id: id }));
			debouncedUpdate(id, value);
		}
	};

	return {
		count: item ? item.count.toString() : '0',
		handleChange,
		error,
	};
};

export const useRemoveProduct = () => {
	const dispatch = useAppDispatch();
	const { dialog } = useDialog();

	const removeProduct = (id: string) => {
		dialog({
			title: 'Удаление товара',
			text: 'Вы хотите убрать товар из корзины?',
			confirmText: 'Удалить товар',
			confirm: () => dispatch(removeFromCart(id)),
			declineText: 'Оставить',
		});
	};

	return {
		removeProduct,
	};
};
